/**
 * GeoJSON map registry.
 *
 * All maps live in `public/geo/` and are fetched lazily the first time a
 * component requests them. Once fetched, the GeoJSON is registered with
 * ECharts (`registerMap`) and cached in memory so subsequent renders are
 * instant.
 */

import { echarts } from './echarts-setup';

/* ── Catalog ───────────────────────────────────────────────────────── */

interface MapEntry {
  /** Path relative to the public root (served by Vite / CDN). */
  url: string;
  /** GeoJSON feature property used for data joining. Defaults to `"name"`. */
  nameProperty?: string;
}

const MAP_CATALOG: Record<string, MapEntry> = {
  USA:            { url: '/geo/usa.json' },
  Europe:         { url: '/geo/europe.json', nameProperty: 'NAME' },
  California:     { url: '/geo/california-districts.json', nameProperty: 'Name' },
  Texas:          { url: '/geo/texas-districts.json', nameProperty: 'District' },
  SanFrancisco:   { url: '/geo/sf-neighborhoods.json' },
  CaliforniaZips: { url: '/geo/california-zips.json', nameProperty: 'ZCTA5CE10' },
  TexasZips:      { url: '/geo/texas-zips.json', nameProperty: 'ZCTA5CE10' },
};

/* ── Registration state ────────────────────────────────────────────── */

const registered = new Set<string>();
const pending = new Map<string, Promise<void>>();

/**
 * Ensure a map is registered with ECharts before rendering.
 * De-duplicates concurrent requests for the same map.
 */
export async function ensureMapRegistered(mapName: string): Promise<void> {
  if (registered.has(mapName)) return;
  if (pending.has(mapName)) return pending.get(mapName);

  const entry = MAP_CATALOG[mapName];
  if (!entry) throw new Error(`Unknown map: "${mapName}". Available: ${Object.keys(MAP_CATALOG).join(', ')}`);

  const promise = fetch(entry.url)
    .then((r) => {
      if (!r.ok) throw new Error(`Failed to fetch ${entry.url}: ${r.status}`);
      return r.json();
    })
    .then((json) => {
      echarts.registerMap(mapName, json as Parameters<typeof echarts.registerMap>[1]);
      registered.add(mapName);
    });

  pending.set(mapName, promise);
  return promise;
}

/**
 * Return the GeoJSON feature property used for data joining for this map,
 * or `undefined` to use the ECharts default (`"name"`).
 */
export function getMapNameProperty(mapName: string): string | undefined {
  return MAP_CATALOG[mapName]?.nameProperty;
}

/** Union of all known map names. */
export type GeoMapName = keyof typeof MAP_CATALOG;

/** List of available map names (for documentation / validation). */
export const GEO_MAP_NAMES = Object.keys(MAP_CATALOG) as GeoMapName[];
